//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { STRIPE_API_PUBLISHABLE_KEY } from '../../system/constants';
// import { LayoutContext, AppContext } from '../../common/contexts';
import { AppContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import Subscription from './Subscription';
import TransactionHistory from './TransactionHistory';
import TermsOfUse from './TermsOfUse';
import BillingInfo from './BillingInfo';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const tabTitleStyles = {
        fontSize: '18px',
        color: '#000',
        marginBottom: 5,
        letterSpacing: 0,
        fontWeight: 600,
        textAlign: 'right',
        // '& span:nth-of-type(1)': { textTransform: 'capitalize !important' },
        textTransform: 'none',
    };

    return makeStyles(theme => ({
        root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 0,
            // marginTop: 30,
            '& .MuiBox-root': { paddingLeft: '0px', paddingRight: '0px' },
            '& .Mui-selected': { color: '#1D8A8B' },
            '& .MuiTabs-indicator': { height: '4px' },
            '& .MuiTabs-flexContainer': { borderBottom: '1px solid #F0F2F6' },
            '& .MuiTabs-flexContainer .MuiButtonBase-root': { marginRight: '30px', padding: 0, minWidth: '3rem' },
        },

        subscriptionTab: { ...tabTitleStyles },
        billingTab: { ...tabTitleStyles },
        transactionHistoryTab: { ...tabTitleStyles },
        termsAndConditionTab: { ...tabTitleStyles },

        tabPanelsContainer: { flex: 1, overflow: 'hidden', paddingTop: '25px' },
        tabPanel: { height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        tabPanelBox: { height: '100%', overflow: 'hidden' },
        buttonBox: { display: 'flex', justifyContent: 'space-between', marginBottom: 20 },
        applyNowButton: {
            boxShadow: 'none',
            background: '#FC821F',
            borderRadius: '4px',
            color: '#fff',
            padding: '12px 40px',
            marginRight: 10,
            marginBottom: 5,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#fc9b4b' },
        },
        applySaveJob: {
            boxShadow: 'none',
            borderRadius: '4px',
            backgroundColor: ' #F0F2F6',
            padding: '12px 40px',
            marginBottom: 5,
            textTransform: 'none',
            color: '#000',
            '&:hover': { backgroundColor: '#c0c1c4' },
        },
        applyButton: {
            boxShadow: 'none',
            background: '#1D8A8B',
            borderRadius: '4px',
            color: '#fff',
            padding: '12px 40px',
            marginBottom: 5,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#4aa1a2' },
        },
        content: {
            overflowY: 'scroll',
            '&::-webkit-scrollbar': { width: '0.4em' },
            '&::-webkit-scrollbar-track': { '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,.1)', borderRadius: '5px' },
        },

        heading: { fontWeight: 'bold', paddingBottom: 10 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, pendingAccountSetup, value, changeTabHandler } = params;

    const heading = pendingAccountSetup ? 'Step 2: Subscription and Payment' : 'Subscription and Billing';

    const defns = {
        appBar: { position: 'static', style: { background: '#fff' }, elevation: '0' },

        subscriptionTab: {
            id: `subscription-billing-tab-0`,
            'aria-controls': `subscription-billing-tab-panel-0`,
        },
        billingTab: {
            id: `subscription-billing-tab-1`,
            'aria-controls': `subscription-billing-tab-panel-1`,
        },
        transactionHistoryTab: {
            id: `subscription-billing-tab-2`,
            'aria-controls': `subscription-billing-tab-panel-2`,
        },
        termsAndConditionTab: {
            id: `subscription-billing-tab-3`,
            'aria-controls': `subscription-billing-tab-panel-3`,
        },
        tabs: { value, onChange: changeTabHandler, 'aria-label': 'subscription-billing tabs' },
        heading: { variant: 'h6', value: heading },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(STRIPE_API_PUBLISHABLE_KEY);

const TabPanel = props => {
    const classes = useStyles()();
    const { children, value, index, ...otherProps } = props;

    const isHidden = value !== index;

    const getDefns = () => {
        const defns = {
            tabPanel: {
                id: `subscription-billing-tab-panel-${index}`,
                role: 'tabpanel',
                'aria-labelledby': `subscription-billing-tab-${index}`,
            },
        };

        // inject classes
        setClassNameInDefns(defns, classes);

        return defns;
    };

    if (isHidden) return null;
    const defns = getDefns();

    return (
        <div {...defns.tabPanel} {...otherProps}>
            {value === index && children}
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const SubscriptionAndBillingTabs = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const appContext = useContext(AppContext);
    const { currentEmployer, pendingAccountSetup } = appContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [value, setValue] = useState(0);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const tier = currentEmployer?.tier;

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, pendingAccountSetup, value, changeTabHandler: handleChange });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Elements stripe={stripePromise}>
            <div {...defns.root}>
                <AfitdTypography {...defns.heading} />
                <AppBar {...defns.appBar}>
                    <Tabs {...defns.tabs}>
                        <Tab label="Subscription" {...defns.subscriptionTab} />
                        {!pendingAccountSetup && tier !== 1 ? <Tab label="My Billing Info" {...defns.billingTab} /> : null}
                        {!pendingAccountSetup && tier !== 1 ? <Tab label="Transaction History" {...defns.transactionHistoryTab} /> : null}
                        <Tab label="Terms of Use" {...defns.termsAndConditionTab} />
                    </Tabs>
                </AppBar>
                <div {...defns.tabPanelsContainer}>
                    <TabPanel value={value} index={0}>
                        <Subscription />
                    </TabPanel>
                    {!pendingAccountSetup && tier !== 1 ? (
                        <TabPanel value={value} index={1}>
                            <BillingInfo />
                        </TabPanel>
                    ) : (
                        <TabPanel value={value} index={1}>
                            <TermsOfUse />
                        </TabPanel>
                    )}
                    {!pendingAccountSetup && tier !== 1 && (
                        <TabPanel value={value} index={2}>
                            <TransactionHistory />
                        </TabPanel>
                    )}
                    <TabPanel value={value} index={3}>
                        <TermsOfUse />
                    </TabPanel>
                </div>
            </div>
        </Elements>
    );
};

export default SubscriptionAndBillingTabs;
